import { useEffect, useState } from 'react'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import {
  descartarPendente, listarPendentes, listarRDOsPendentes, deletarRDOSincronizado,
  type PendenteRecord, type RDOPendenteRecord, type PendenteStore,
} from '@/lib/offlineDB'
import { useSyncManager } from '@/hooks/useSyncManager'
import { toast } from 'sonner'
import { Trash2, RefreshCw, Loader2 } from 'lucide-react'

interface Item {
  store: PendenteStore
  id: string
  titulo: string
  subtitulo: string
  criado_em: string
  erro?: string | null
}

const STORE_LABEL: Record<PendenteStore, string> = {
  rdos_pendentes: 'RDO',
  tarefas_pendentes: 'Tarefa',
  fvs_pendentes: 'FVS',
  fvm_pendentes: 'FVM',
  fotos_pendentes: 'Foto',
}

export default function PendentesModal({
  open, onOpenChange,
}: { open: boolean; onOpenChange: (v: boolean) => void }) {
  const { pendentes, syncing, progresso, runSync, isOnline, refreshCount } = useSyncManager()
  const [items, setItems] = useState<Item[]>([])
  const [loading, setLoading] = useState(false)

  const load = async () => {
    setLoading(true)
    const acc: Item[] = []
    try {
      // RDOs (formato legado)
      const rdos: RDOPendenteRecord[] = await listarRDOsPendentes()
      for (const r of rdos) acc.push({
        store: 'rdos_pendentes', id: r.id,
        titulo: `RDO de ${r.data_registro}`,
        subtitulo: r.frente_servico || r.servicos_executados?.slice(0, 60) || 'Sem descrição',
        criado_em: r.criado_offline_em, erro: r.ultimo_erro,
      })
      // Tarefas
      const tarefas = await listarPendentes<any>('tarefas_pendentes')
      for (const r of tarefas) acc.push({
        store: 'tarefas_pendentes', id: r.id,
        titulo: `Tarefa → ${r.data?.novo_status || 'update'}`,
        subtitulo: `ID: ${r.data?.tarefa_id?.slice(0, 8) || '—'}`,
        criado_em: r.criado_offline_em, erro: r.ultimo_erro,
      })
      // FVS
      const fvs = await listarPendentes<any>('fvs_pendentes')
      for (const r of fvs) acc.push({
        store: 'fvs_pendentes', id: r.id,
        titulo: r.data?.titulo || 'FVS',
        subtitulo: `${r.data?.disciplina || ''} · ${r.data?.itens?.length || 0} itens`,
        criado_em: r.criado_offline_em, erro: r.ultimo_erro,
      })
      // FVM
      const fvm = await listarPendentes<any>('fvm_pendentes')
      for (const r of fvm) acc.push({
        store: 'fvm_pendentes', id: r.id,
        titulo: r.data?.material || 'FVM',
        subtitulo: r.data?.fornecedor || '',
        criado_em: r.criado_offline_em, erro: r.ultimo_erro,
      })
      // Fotos
      const fotos = await listarPendentes<any>('fotos_pendentes')
      for (const r of fotos) acc.push({
        store: 'fotos_pendentes', id: r.id,
        titulo: r.data?.titulo || r.data?.nome_arquivo || 'Foto',
        subtitulo: `${Math.round((r.data?.tamanho_bytes || 0) / 1024)} KB`,
        criado_em: r.criado_offline_em, erro: r.ultimo_erro,
      })
    } catch (e: any) {
      toast.error('Erro ao listar pendentes: ' + (e?.message || ''))
    }
    acc.sort((a, b) => a.criado_em.localeCompare(b.criado_em))
    setItems(acc)
    setLoading(false)
  }

  useEffect(() => { if (open) void load() }, [open, pendentes])

  const handleDescartar = async (it: Item) => {
    if (!confirm(`Descartar "${it.titulo}"? Os dados offline serão perdidos.`)) return
    try {
      if (it.store === 'rdos_pendentes') await deletarRDOSincronizado(it.id)
      else await descartarPendente(it.store, it.id)
      toast.success('Item descartado')
      await refreshCount()
      await load()
    } catch (e: any) {
      toast.error('Erro: ' + (e?.message || ''))
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            ⏳ Itens pendentes de sincronização
            <span className="text-xs font-medium text-muted-foreground">({pendentes})</span>
          </DialogTitle>
        </DialogHeader>

        <div className="flex items-center justify-between gap-2 py-2 border-b border-border">
          <div className="text-xs text-muted-foreground">
            {!isOnline && '⚠️ Você está offline. Sync ocorrerá ao reconectar.'}
            {isOnline && syncing && progresso && (
              <span className="flex items-center gap-1.5">
                <Loader2 className="w-3 h-3 animate-spin" />
                Sincronizando {progresso.etapa}... ({progresso.atual}/{progresso.total})
              </span>
            )}
            {isOnline && !syncing && pendentes > 0 && 'Pronto para sincronizar.'}
            {isOnline && !syncing && pendentes === 0 && '✅ Nada pendente.'}
          </div>
          <Button
            size="sm"
            disabled={!isOnline || syncing || pendentes === 0}
            onClick={() => runSync(true)}
            className="bg-primary text-primary-foreground"
          >
            <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${syncing ? 'animate-spin' : ''}`} />
            Sincronizar agora
          </Button>
        </div>

        <div className="flex-1 overflow-y-auto py-2 space-y-1.5">
          {loading ? (
            <div className="text-center py-8 text-sm text-muted-foreground">Carregando...</div>
          ) : items.length === 0 ? (
            <div className="text-center py-12 text-sm text-muted-foreground">
              Nenhum item pendente de sincronização.
            </div>
          ) : (
            items.map(it => (
              <div
                key={`${it.store}-${it.id}`}
                className="flex items-center gap-3 p-2.5 rounded-lg border border-border bg-card"
              >
                <span
                  className="text-[10px] font-bold px-2 py-0.5 rounded"
                  style={{ background: 'hsl(var(--amber-bg))', color: 'hsl(var(--amber))' }}
                >
                  {STORE_LABEL[it.store]}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-foreground truncate">{it.titulo}</div>
                  <div className="text-xs text-muted-foreground truncate">
                    {it.subtitulo} · {new Date(it.criado_em).toLocaleString('pt-BR')}
                  </div>
                  {it.erro && (
                    <div className="text-[10px] mt-0.5" style={{ color: 'hsl(var(--rose))' }}>
                      Erro: {it.erro.slice(0, 80)}
                    </div>
                  )}
                </div>
                <Button
                  size="sm" variant="ghost"
                  onClick={() => handleDescartar(it)}
                  title="Descartar"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            ))
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
